import { useQuery } from '@tanstack/react-query';
import { pinAdvertisementInput } from '@trafiku/contracts';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { ImageUpload } from '@/components/image-upload';
import { MapView } from '@/components/map/map-view';
import { OverviewMap } from '@/components/map/overview-map';
import { PointPicker } from '@/components/map/point-picker';
import { ResourceList } from '@/components/resource-list';
import { DailyBars } from '@/components/stats';
import { Field, Input, Select, Switch, Textarea } from '@/components/ui/form';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/layout';
import { api } from '@/lib/api';
import { useFormState } from '@/lib/form';
import { t } from '@/lib/i18n';
import { pointsCollection } from '@/lib/network';
import { useItem, useSave } from '@/lib/resources';
import { type PinAdvertisement, refId } from '@/lib/types';
import { CampaignSelect } from './advertisements';

export function PinAdvertisementsPage() {
  return (
    <ResourceList<PinAdvertisement>
      title={t('Map pins')}
      subtitle={
        <>
          {t('Advertiser pins on the app map.')}{' '}
          <Badge tone="amber">{t('Currently switched off in the app API')}</Badge>
        </>
      }
      map={({ rows, hovered, hover }) => (
        <OverviewMap
          data={pointsCollection(
            rows,
            (p) => p.geometry?.coordinates as [number, number] | undefined,
            (p) => p.name,
            '#db2777',
          )}
          kind="point"
          hovered={hovered}
          onHover={hover}
          basePath="/pin-advertisements"
        />
      )}
      resource="pin-advertisements"
      basePath="/pin-advertisements"
      newLabel={t('New pin')}
      columns={[
        { header: t('Pin'), cell: (p) => <span className="font-medium">{p.name}</span> },
        {
          header: t('Campaign'),
          cell: (p) => (typeof p.campaign_id === 'object' ? p.campaign_id?.name : '—'),
        },
        { header: t('Views'), cell: (p) => p.views_count },
        { header: t('Clicks'), cell: (p) => p.clicks_count },
        {
          header: t('Status'),
          cell: (p) => (p.is_active ? <Badge tone="green">{t('Active')}</Badge> : <Badge>{t('Off')}</Badge>),
        },
      ]}
    />
  );
}

function PinStats({ id }: { id: string }) {
  const [type, setType] = useState<'view' | 'click'>('click');
  const stats = useQuery({
    queryKey: ['pin-advertisements', 'stats', id, type],
    queryFn: () =>
      api<{ by_day: Array<{ day: string; count: number }> }>(`/pin-advertisements/${id}/stats`, {
        query: { type },
      }),
  });
  return (
    <Card
      title={t('Statistics')}
      actions={
        <Select className="w-32" value={type} onChange={(e) => setType(e.target.value as 'view' | 'click')}>
          <option value="click">{t('Clicks')}</option>
          <option value="view">{t('Views')}</option>
        </Select>
      }
    >
      {stats.isLoading ? <Spinner /> : <DailyBars data={stats.data?.by_day ?? []} />}
    </Card>
  );
}

export function PinAdvertisementEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<PinAdvertisement>('pin-advertisements', id);
  const save = useSave<PinAdvertisement>('pin-advertisements', id);
  const navigate = useNavigate();
  const form = useFormState({
    name: '',
    description: '',
    web_view: '',
    icon: '',
    pin_icon: '',
    background_image: '',
    priority: 1,
    is_active: false,
    campaign_id: '',
    geometry: null as PinAdvertisement['geometry'] | null,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    const p = item.data;
    if (p)
      form.setValues({
        name: p.name,
        description: p.description ?? '',
        web_view: p.web_view,
        icon: p.icon ?? '',
        pin_icon: p.pin_icon ?? '',
        background_image: p.background_image ?? '',
        priority: p.priority,
        is_active: p.is_active,
        campaign_id: refId(p.campaign_id),
        geometry: p.geometry,
      });
  }, [item.data]);
  if (!isNew && item.isLoading) return <Spinner />;
  const { values, set, errors } = form;
  const pos = values.geometry?.coordinates as [number, number] | undefined;
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(pinAdvertisementInput, (d) => save.mutateAsync(d)))
          navigate('/pin-advertisements');
      }}
    >
      <PageHeader title={isNew ? t('New map pin') : values.name} back="/pin-advertisements" />
      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <Card>
          <div className="space-y-4">
            <Field label={t('Name')} error={errors.name}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.name}
                  onChange={(e) => set('name', e.target.value)}
                  invalid={!!errors.name}
                />
              )}
            </Field>
            <Field label={t('Campaign')} error={errors.campaign_id}>
              {() => (
                <CampaignSelect
                  value={values.campaign_id}
                  onChange={(v) => set('campaign_id', v)}
                  invalid={!!errors.campaign_id}
                />
              )}
            </Field>
            <Field label={t('Web page shown when tapped')} error={errors.web_view}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.web_view}
                  onChange={(e) => set('web_view', e.target.value)}
                  placeholder="https://"
                  invalid={!!errors.web_view}
                />
              )}
            </Field>
            <Field label={t('Pin icon (on the map)')}>
              {() => (
                <ImageUpload
                  kind="pinAdvertisementPinIcons"
                  square
                  value={values.pin_icon}
                  onChange={(v) => set('pin_icon', v)}
                />
              )}
            </Field>
            <Field label={t('Logo')}>
              {() => (
                <ImageUpload
                  kind="pinAdvertisementIcons"
                  square
                  value={values.icon}
                  onChange={(v) => set('icon', v)}
                />
              )}
            </Field>
            <Field label={t('Background image')}>
              {() => (
                <ImageUpload
                  kind="pinAdvertisementImages"
                  value={values.background_image}
                  onChange={(v) => set('background_image', v)}
                />
              )}
            </Field>
            <Field label={t('Priority (1–20)')} error={errors.priority}>
              {(fid) => (
                <Input
                  id={fid}
                  type="number"
                  min={1}
                  max={20}
                  value={values.priority}
                  onChange={(e) => set('priority', Number(e.target.value))}
                  className="w-24"
                />
              )}
            </Field>
            <Field label={t('Description')}>
              {(fid) => (
                <Textarea
                  id={fid}
                  value={values.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('Position')} error={errors.geometry}>
              {() => (
                <p className="font-mono text-xs text-slate-600">
                  {pos ? `${pos[1].toFixed(6)}, ${pos[0].toFixed(6)}` : t('Click on the map')}
                </p>
              )}
            </Field>
            <Switch checked={values.is_active} onChange={(v) => set('is_active', v)} label={t('Active')} />
          </div>
          <div className="mt-6">
            <FormActions
              saving={save.isPending}
              resource="pin-advertisements"
              id={id}
              backTo="/pin-advertisements"
            />
          </div>
        </Card>
        <div className="space-y-6">
          <MapView className="h-[480px]" center={pos} zoom={pos ? 16 : 12.5}>
            <PointPicker value={pos} onChange={(p) => set('geometry', { type: 'Point', coordinates: p })} />
          </MapView>
          {!isNew && id && <PinStats id={id} />}
        </div>
      </div>
    </form>
  );
}
